/**
 * Organization Page
 * @description 팀/사용자 관리 페이지
 */

import { useState, useEffect } from 'react';
import './tenant-admin.css';
import {
  Badge, Button, Card, Checkbox, Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogTrigger, Input, Label, Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue, Tabs, TabsContent, TabsList, TabsTrigger
} from '@shared/ui/primitives';
import { useAuthStore } from '@/stores/authStore';
import { dataService } from '@/services/dataService';
import {
    Plus,
    Edit,
    Trash2,
    Users,
    User,
    Mail,
    Building
} from 'lucide-react';

interface Team {
    teamId: string;
    teamName: string;
    status: 'active' | 'inactive';
}

interface User {
    userId: string;
    displayName: string;
    email: string;
    position?: string;
    teamIds: string[];
    status: 'active' | 'inactive';
}

interface OrgData {
    teams: Team[];
    users: User[];
}

export const OrganizationPage: React.FC = () => {
    const { user } = useAuthStore();
    const [loading, setLoading] = useState(false);
    const [orgData, setOrgData] = useState<OrgData>({ teams: [], users: [] });
    const [teamModalOpen, setTeamModalOpen] = useState(false);
    const [userModalOpen, setUserModalOpen] = useState(false);
    const [editingTeam, setEditingTeam] = useState<Team | null>(null);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [selectedTeamFilter, setSelectedTeamFilter] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState<string>('');

    // Form states
    const [teamForm, setTeamForm] = useState<Partial<Team>>({});
    const [userForm, setUserForm] = useState<Partial<User>>({});

    useEffect(() => {
        const currentUser = user;
        if (currentUser?.userType === 'tenant-user') {
            loadOrgData(currentUser.tenantId);
        }
    }, [user]);

    const loadOrgData = async (tenantId: string) => {
        try {
            setLoading(true);

            // 실제 API 호출
            const data = await dataService.getTenantOrganization(tenantId);

            setOrgData({
                teams: data.teams || [],
                users: data.users || []
            });
        } catch (error) {
            console.error('조직 정보를 불러오는데 실패했습니다:', error);

            // 에러 시 빈 데이터 사용
            setOrgData({
                teams: [],
                users: []
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSaveOrg = async (updatedData: OrgData) => {
        const currentUser = user;
        if (currentUser?.userType !== 'tenant-user') return;

        try {
            await dataService.updateTenantOrganization(currentUser.tenantId, updatedData);
            setOrgData(updatedData);
            console.log('조직 데이터가 성공적으로 저장되었습니다.');
        } catch (error) {
            console.error('저장에 실패했습니다:', error);
        }
    };

    // Team handlers
    const handleTeamCreate = () => {
        setEditingTeam(null);
        setTeamForm({});
        setTeamModalOpen(true);
    };

    const handleTeamEdit = (team: Team) => {
        setEditingTeam(team);
        setTeamForm(team);
        setTeamModalOpen(true);
    };

    const handleTeamDelete = (teamId: string) => {
        if (confirm(`정말로 팀 "${teamId}"를 삭제하시겠습니까?`)) {
            const updatedTeams = orgData.teams.filter(t => t.teamId !== teamId);
            handleSaveOrg({ ...orgData, teams: updatedTeams });
        }
    };

    const handleTeamSubmit = () => {
        if (!teamForm.teamId || !teamForm.teamName) return;

        let updatedTeams = [...orgData.teams];
        if (editingTeam) {
            const index = updatedTeams.findIndex(t => t.teamId === editingTeam.teamId);
            updatedTeams[index] = { ...updatedTeams[index], ...teamForm } as Team;
        } else {
            updatedTeams.push({
                ...teamForm,
                status: teamForm.status || 'active'
            } as Team);
        }

        handleSaveOrg({ ...orgData, teams: updatedTeams });
        setTeamModalOpen(false);
        setTeamForm({});
    };

    // User handlers
    const handleUserCreate = () => {
        setEditingUser(null);
        setUserForm({ teamIds: [] });
        setUserModalOpen(true);
    };

    const handleUserEdit = (user: User) => {
        setEditingUser(user);
        setUserForm(user);
        setUserModalOpen(true);
    };

    const handleUserDelete = (userId: string) => {
        if (confirm(`정말로 사용자 "${userId}"를 삭제하시겠습니까?`)) {
            const updatedUsers = orgData.users.filter(u => u.userId !== userId);
            handleSaveOrg({ ...orgData, users: updatedUsers });
        }
    };

    const handleUserSubmit = () => {
        if (!userForm.userId || !userForm.displayName || !userForm.email) return;

        let updatedUsers = [...orgData.users];
        if (editingUser) {
            const index = updatedUsers.findIndex(u => u.userId === editingUser.userId);
            updatedUsers[index] = { ...updatedUsers[index], ...userForm } as User;
        } else {
            updatedUsers.push({
                ...userForm,
                status: userForm.status || 'active',
                teamIds: userForm.teamIds || []
            } as User);
        }

        handleSaveOrg({ ...orgData, users: updatedUsers });
        setUserModalOpen(false);
        setUserForm({});
    };

    // 필터링된 사용자 목록 계산
    const filteredUsers = selectedTeamFilter === 'all'
        ? orgData.users
        : orgData.users.filter(user => user.teamIds?.includes(selectedTeamFilter));

    return (
        <div className="tenant-admin-page space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">조직/사용자 관리</h1>
                <p className="text-gray-600 mt-1">
                    팀과 사용자를 관리하고 조직 구조를 설정할 수 있습니다.
                </p>
            </div>

            {!user || user.userType !== 'tenant-user' ? (
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <p className="text-gray-500">테넌트 사용자로 로그인해주세요.</p>
                    </div>
                </div>
            ) : loading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            ) : (

                <Tabs defaultValue="teams" className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="teams" className="flex items-center gap-2">
                            <Building className="h-4 w-4" />
                            팀 관리
                        </TabsTrigger>
                        <TabsTrigger value="users" className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            사용자 관리
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="teams" className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h2 className="text-lg font-semibold">팀 목록</h2>
                            <Dialog open={teamModalOpen} onOpenChange={setTeamModalOpen}>
                                <DialogTrigger asChild>
                                    <Button onClick={handleTeamCreate} className="flex items-center gap-2">
                                        <Plus className="h-4 w-4" />
                                        팀 추가
                                    </Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>{editingTeam ? '팀 수정' : '팀 추가'}</DialogTitle>
                                        <DialogDescription>
                                            {editingTeam ? '기존 팀의 정보를 수정합니다.' : '새로운 팀을 생성합니다.'}
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                        <div>
                                            <Label htmlFor="teamId">Team ID</Label>
                                            <Input
                                                id="teamId"
                                                value={teamForm.teamId || ''}
                                                onChange={(e) => setTeamForm({ ...teamForm, teamId: e.target.value })}
                                                disabled={!!editingTeam}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="teamName">Team Name</Label>
                                            <Input
                                                id="teamName"
                                                value={teamForm.teamName || ''}
                                                onChange={(e) => setTeamForm({ ...teamForm, teamName: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="status">상태</Label>
                                            <Select
                                                value={teamForm.status || 'active'}
                                                onValueChange={(value) => setTeamForm({ ...teamForm, status: value as 'active' | 'inactive' })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="active">활성</SelectItem>
                                                    <SelectItem value="inactive">비활성</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <Button variant="outline" onClick={() => setTeamModalOpen(false)}>
                                                취소
                                            </Button>
                                            <Button onClick={handleTeamSubmit}>
                                                저장
                                            </Button>
                                        </div>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>

                        <Card className="shadow-sm">
                            <div className="p-6">
                                <div className="space-y-3">
                                    {orgData.teams.map((team) => (
                                        <div key={team.teamId} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all duration-200">
                                            <div className="flex items-center gap-4">
                                                <div className="p-2.5 bg-blue-50 rounded-lg border border-blue-100">
                                                    <Building className="h-5 w-5 text-blue-600" />
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="text-sm font-semibold text-gray-900">{team.teamName}</h3>
                                                    <p className="text-xs text-gray-500 mt-0.5">{team.teamId}</p>
                                                </div>
                                                <Badge
                                                    variant={team.status === 'active' ? 'default' : 'secondary'}
                                                    className="text-xs px-2 py-1"
                                                >
                                                    {team.status === 'active' ? '활성' : '비활성'}
                                                </Badge>
                                            </div>
                                            <div className="flex gap-1.5">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 hover:bg-blue-50 hover:border-blue-200"
                                                    onClick={() => handleTeamEdit(team)}
                                                >
                                                    <Edit className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 hover:bg-red-50 hover:border-red-200"
                                                    onClick={() => handleTeamDelete(team.teamId)}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                    {orgData.teams.length === 0 && (
                                        <div className="text-center py-12 text-gray-500">
                                            <Building className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                                            <p className="text-sm">등록된 팀이 없습니다.</p>
                                            <p className="text-xs text-gray-400 mt-1">새 팀을 추가해보세요.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Card>
                    </TabsContent>

                    <TabsContent value="users" className="space-y-4">
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center gap-4">
                                <div>
                                    <h2 className="text-lg font-semibold">사용자 목록</h2>
                                    <p className="text-sm text-gray-500">
                                        {selectedTeamFilter === 'all'
                                            ? `전체 ${filteredUsers.length}명의 사용자`
                                            : `${orgData.teams.find(t => t.teamId === selectedTeamFilter)?.teamName || '선택된 팀'}에서 ${filteredUsers.length}명의 사용자`
                                        }
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-500">팀 필터:</span>
                                    <Select value={selectedTeamFilter} onValueChange={setSelectedTeamFilter}>
                                        <SelectTrigger className="w-40">
                                            <SelectValue placeholder="팀 선택" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white border border-gray-200 shadow-lg z-50 max-h-60 overflow-y-auto">
                                            <SelectItem value="all" className="hover:bg-gray-50 focus:bg-gray-50">
                                                전체 ({orgData.users.length}명)
                                            </SelectItem>
                                            {orgData.teams.map((team) => {
                                                const teamUserCount = orgData.users.filter(user =>
                                                    user.teamIds?.includes(team.teamId)
                                                ).length;
                                                return (
                                                    <SelectItem
                                                        key={team.teamId}
                                                        value={team.teamId}
                                                        className="hover:bg-gray-50 focus:bg-gray-50"
                                                    >
                                                        {team.teamName} ({teamUserCount}명)
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <Dialog open={userModalOpen} onOpenChange={setUserModalOpen}>
                                <DialogTrigger asChild>
                                    <Button onClick={handleUserCreate} className="flex items-center gap-2">
                                        <Plus className="h-4 w-4" />
                                        사용자 추가
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-md">
                                    <DialogHeader>
                                        <DialogTitle>{editingUser ? '사용자 수정' : '사용자 추가'}</DialogTitle>
                                        <DialogDescription>
                                            {editingUser ? '기존 사용자의 정보를 수정합니다.' : '새로운 사용자를 생성하고 팀에 배정합니다.'}
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                        <div>
                                            <Label htmlFor="userId">User ID</Label>
                                            <Input
                                                id="userId"
                                                value={userForm.userId || ''}
                                                onChange={(e) => setUserForm({ ...userForm, userId: e.target.value })}
                                                disabled={!!editingUser}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="displayName">Display Name</Label>
                                            <Input
                                                id="displayName"
                                                value={userForm.displayName || ''}
                                                onChange={(e) => setUserForm({ ...userForm, displayName: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="email">Email</Label>
                                            <Input
                                                id="email"
                                                type="email"
                                                value={userForm.email || ''}
                                                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="position">Position</Label>
                                            <Input
                                                id="position"
                                                value={userForm.position || ''}
                                                onChange={(e) => setUserForm({ ...userForm, position: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="status">상태</Label>
                                            <Select
                                                value={userForm.status || 'active'}
                                                onValueChange={(value) => setUserForm({ ...userForm, status: value as 'active' | 'inactive' })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="active">활성</SelectItem>
                                                    <SelectItem value="inactive">비활성</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label>팀 할당</Label>
                                            <div className="mt-2 max-h-60 overflow-y-auto border rounded p-3 space-y-2">
                                                {orgData.teams.length === 0 ? (
                                                    <p className="text-sm text-gray-500 text-center py-4">
                                                        등록된 팀이 없습니다. 먼저 팀을 생성해주세요.
                                                    </p>
                                                ) : (
                                                    orgData.teams.map((team) => {
                                                        const isSelected = (userForm.teamIds || []).includes(team.teamId);
                                                        return (
                                                            <label
                                                                key={team.teamId}
                                                                className="flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-50 rounded"
                                                            >
                                                                <Checkbox
                                                                    checked={isSelected}
                                                                    onCheckedChange={(checked) => {
                                                                        const currentTeamIds = userForm.teamIds || [];
                                                                        if (checked) {
                                                                            // 팀 추가
                                                                            setUserForm({
                                                                                ...userForm,
                                                                                teamIds: [...currentTeamIds, team.teamId]
                                                                            });
                                                                        } else {
                                                                            // 팀 제거
                                                                            setUserForm({
                                                                                ...userForm,
                                                                                teamIds: currentTeamIds.filter(id => id !== team.teamId)
                                                                            });
                                                                        }
                                                                    }}
                                                                />
                                                                <div className="flex-1">
                                                                    <span className="text-sm font-medium text-gray-900">{team.teamName}</span>
                                                                    <span className="text-xs text-gray-500 ml-2">({team.teamId})</span>
                                                                </div>
                                                                <Badge
                                                                    variant={team.status === 'active' ? 'default' : 'secondary'}
                                                                    className="text-xs"
                                                                >
                                                                    {team.status === 'active' ? '활성' : '비활성'}
                                                                </Badge>
                                                            </label>
                                                        );
                                                    })
                                                )}
                                            </div>
                                            {(userForm.teamIds || []).length > 0 && (
                                                <p className="mt-2 text-xs text-gray-500">
                                                    {(userForm.teamIds || []).length}개의 팀에 할당됨
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <Button variant="outline" onClick={() => setUserModalOpen(false)}>
                                                취소
                                            </Button>
                                            <Button onClick={handleUserSubmit}>
                                                저장
                                            </Button>
                                        </div>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>

                        <Card className="shadow-sm">
                            <div className="p-6">
                                <div className="space-y-3">
                                    {filteredUsers.map((user) => (
                                        <div key={user.userId} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all duration-200">
                                            <div className="flex items-center gap-4 flex-1">
                                                <div className="p-2.5 bg-green-50 rounded-lg border border-green-100">
                                                    <User className="h-5 w-5 text-green-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 mb-1">
                                                        <h3 className="text-sm font-semibold text-gray-900 truncate">{user.displayName}</h3>
                                                        {user.position && (
                                                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full whitespace-nowrap">
                                                                {user.position}
                                                            </span>
                                                        )}
                                                        <Badge
                                                            variant={user.status === 'active' ? 'default' : 'secondary'}
                                                            className="text-xs px-2 py-1"
                                                        >
                                                            {user.status === 'active' ? '활성' : '비활성'}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-xs text-gray-500 mb-1">{user.userId}</p>
                                                    <div className="flex items-center gap-2">
                                                        <Mail className="h-3 w-3 text-gray-400 flex-shrink-0" />
                                                        <span className="text-xs text-gray-500 truncate">{user.email}</span>
                                                    </div>
                                                    {user.teamIds && user.teamIds.length > 0 && (
                                                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                                                            <span className="text-xs text-gray-400">팀:</span>
                                                            {user.teamIds.map((teamId) => {
                                                                const team = orgData.teams.find(t => t.teamId === teamId);
                                                                return (
                                                                    <span 
                                                                        key={teamId} 
                                                                        className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 cursor-pointer transition-colors"
                                                                        title="클릭하여 팀에서 제거"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            if (confirm(`"${team?.teamName || teamId}" 팀에서 이 사용자를 제거하시겠습니까?`)) {
                                                                                const updatedUsers = orgData.users.map(u => 
                                                                                    u.userId === user.userId
                                                                                        ? { ...u, teamIds: u.teamIds?.filter(id => id !== teamId) || [] }
                                                                                        : u
                                                                                );
                                                                                handleSaveOrg({ ...orgData, users: updatedUsers });
                                                                            }
                                                                        }}
                                                                    >
                                                                        {team?.teamName || teamId}
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                    {(!user.teamIds || user.teamIds.length === 0) && (
                                                        <div className="flex items-center gap-1 mt-1">
                                                            <span className="text-xs text-gray-400">팀 미할당</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex gap-1.5 ml-4">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 px-2 text-xs hover:bg-green-50 hover:border-green-200"
                                                    onClick={() => {
                                                        // 빠른 팀 할당: 사용자가 속하지 않은 첫 번째 활성 팀에 할당
                                                        const availableTeams = orgData.teams.filter(
                                                            team => team.status === 'active' && !user.teamIds?.includes(team.teamId)
                                                        );
                                                        if (availableTeams.length > 0) {
                                                            const updatedUsers = orgData.users.map(u => 
                                                                u.userId === user.userId
                                                                    ? { ...u, teamIds: [...(u.teamIds || []), availableTeams[0].teamId] }
                                                                    : u
                                                            );
                                                            handleSaveOrg({ ...orgData, users: updatedUsers });
                                                        } else {
                                                            alert('할당 가능한 활성 팀이 없습니다.');
                                                        }
                                                    }}
                                                    title="빠른 팀 할당"
                                                    disabled={orgData.teams.filter(team => team.status === 'active' && !user.teamIds?.includes(team.teamId)).length === 0}
                                                >
                                                    <Users className="h-3.5 w-3.5 mr-1" />
                                                    팀 할당
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 hover:bg-blue-50 hover:border-blue-200"
                                                    onClick={() => handleUserEdit(user)}
                                                    title="사용자 편집"
                                                >
                                                    <Edit className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 hover:bg-red-50 hover:border-red-200"
                                                    onClick={() => handleUserDelete(user.userId)}
                                                    title="사용자 삭제"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                    {filteredUsers.length === 0 && (
                                        <div className="text-center py-12 text-gray-500">
                                            <User className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                                            {selectedTeamFilter === 'all' ? (
                                                <>
                                                    <p className="text-sm">등록된 사용자가 없습니다.</p>
                                                    <p className="text-xs text-gray-400 mt-1">새 사용자를 추가해보세요.</p>
                                                </>
                                            ) : (
                                                <>
                                                    <p className="text-sm">선택한 팀에 사용자가 없습니다.</p>
                                                    <p className="text-xs text-gray-400 mt-1">다른 팀을 선택하거나 전체를 확인해보세요.</p>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Card>
                    </TabsContent>
                </Tabs>
            )}
        </div>
    );
};

OrganizationPage.displayName = 'OrganizationPage';